/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Text 클래스를 정의한다.
 *
 * @file /src/components/text/Text.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 1. 27.
 */
import * as parent from '@base/Component';
import { Dom } from '@utils/Dom';
import './Text.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 텍스트
     */
    text?: string;

    /**
     * HTML태그
     */
    html?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Text extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'text';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'text';

    /**
     * 텍스트
     */
    text: string | null = null;

    /**
     * HTML태그
     */
    html: string | null = null;

    /**
     * Text 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Text> = null) {
        if (typeof properties === 'string') {
            const text = properties;
            properties = { text: text };
        }

        super(properties);
        this.text = this.properties.text ?? '';
        this.html = this.properties.html ?? '';
    }

    /**
     * 컴포넌트 콘텐츠 DOM 을 가져온다.
     * 컴포넌트 종류에 따라 콘텐츠 DOM 타입변경이 필요할 경우, 하위 클래스에서 재정의한다.
     *
     * @returns 콘텐츠 DOM
     */
    $getContent(): Dom {
        return this.$container;
    }

    /**
     * 텍스트객체의 문자열을 설정한다.
     *
     * @param text - 텍스트
     */
    setText(text: string): void {
        this.text = text;
        this.$getContainer().html(text.replace('<', '&lt;').replace('>', '&gt;'));
    }

    /**
     * 텍스트객체의 HTML태그를 설정한다.
     *
     * @param html - HTML태그
     */
    setHtml(html: string): void {
        this.html = html;
        this.$getContainer().html(html);
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        if (this.html) {
            this.$getContainer().html(this.html);
        } else {
            this.$getContainer().text(this.text);
        }
    }
}
